import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../services/authService';
import { useUserStatusCheck } from '../hooks/useUserStatusCheck';
import '../styles/PendingApprovalPage.css';

export const PendingApprovalPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Poll for status changes every 5 seconds with custom handler
  useUserStatusCheck({
    checkInterval: 5000,
    onDeactivated: () => {
      console.log('Pending approval: User was deactivated');
    },
  });

  // Also check for approval specifically on this page
  useEffect(() => {
    if (!user?.uid) return;

    const checkApprovalStatus = async () => {
      try {
        const userData = await AuthService.getUserData(user.uid);
        if (userData && userData.isApproved) {
          console.log('User has been approved! Redirecting to dashboard');
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        console.error('Error checking approval status:', error);
      }
    };

    // Check immediately
    checkApprovalStatus();

    // Then check every 5 seconds
    const interval = setInterval(checkApprovalStatus, 5000);

    return () => clearInterval(interval);
  }, [user?.uid, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="pending-approval-container">
      <div className="pending-card">
        <div className="pending-header">
          <div className="pending-icon">⏳</div>
          <h1>Approval Pending</h1>
        </div>

        <div className="pending-content">
          <p className="pending-message">
            Hello <strong>{user?.name}</strong>!
          </p>
          <p className="pending-description">
            Your account has been created, but it requires approval from an administrator before you can access the system.
          </p>

          <div className="pending-info">
            <div className="info-item">
              <span className="info-label">Email:</span>
              <span className="info-value">{user?.email}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className="info-value pending-status">Pending Review</span>
            </div>
          </div>

          <div className="pending-steps">
            <p className="steps-title">What happens next?</p>
            <ol>
              <li>An administrator will review your account</li>
              <li>You'll receive an email confirmation once approved</li>
              <li>You can then log back in and access the system</li>
            </ol>
          </div>

          <p className="pending-contact">
            If you have any questions, please contact your administrator.
          </p>
        </div>

        <div className="pending-actions">
          <button onClick={handleLogout} className="logout-button">
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};
